import { useState } from 'react';
import { Navigate, useLocation, Link } from 'react-router-dom';
import { Alert, Button, Card, Form, Input, Typography, Result } from 'antd';
import { LockOutlined, UserOutlined, MailOutlined, SafetyOutlined } from '@ant-design/icons';

import { useAuth, forgotPassword, confirmForgotPassword } from '../auth';

const { Text } = Typography;

function traducirErrorCognito(msg: string): string {
  const map: Record<string, string> = {
    'Incorrect username or password.': 'Usuario o contraseña incorrectos.',
    'User does not exist.': 'El usuario no existe.',
    'User is not confirmed.': 'El usuario no ha sido verificado. Revisa tu email.',
    'Password attempts exceeded': 'Demasiados intentos fallidos. Intenta más tarde.',
    'Password does not conform to policy': 'La contraseña no cumple con la política de seguridad.',
    'Code mismatch': 'Código incorrecto.',
    'Expired code': 'El código expiró.',
    'Limit exceeded': 'Límite excedido. Intenta más tarde.',
    'Too many requests': 'Demasiadas solicitudes. Intenta más tarde.',
    'User already exists': 'El usuario ya existe.',
    'Invalid password': 'Contraseña inválida.',
    'Invalid parameter provided. Please check the parameters and try again.': 'Parámetro inválido. Verifica los datos e intenta de nuevo.',
    'Missing required parameter USERNAME': 'Ingresa tu usuario.',
    'Missing required parameter PASSWORD': 'Ingresa tu contraseña.',
    'User not authorized': 'Usuario no autorizado.',
  };
  return map[msg] || msg;
}

interface LoginValues {
  username: string;
  password: string;
}

interface ChallengeValues {
  newPassword: string;
  confirm: string;
}

export function Login() {
  const { user, signIn, completeNewPassword, pendingChallenge } = useAuth();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [recoveryMode, setRecoveryMode] = useState<'login' | 'forgot' | 'confirm' | 'success'>('login');
  const [recoveryEmail, setRecoveryEmail] = useState('');

  if (user) {
    const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? '/';
    return <Navigate to={from} replace />;
  }

  async function onLogin(values: LoginValues) {
    setError(null);
    setLoading(true);
    try {
      await signIn(values.username.trim(), values.password);
    } catch (e: unknown) {
      const raw = e instanceof Error ? e.message : 'No se pudo iniciar sesión';
      setError(traducirErrorCognito(raw));
    } finally {
      setLoading(false);
    }
  }

  async function onChallenge(values: ChallengeValues) {
    setError(null);
    if (values.newPassword !== values.confirm) {
      setError('Las Contraseñas No Coinciden');
      return;
    }
    setLoading(true);
    try {
      await completeNewPassword(values.newPassword);
    } catch (e: unknown) {
      const raw = e instanceof Error ? e.message : 'No se pudo completar el cambio de contraseña';
      setError(traducirErrorCognito(raw));
    } finally {
      setLoading(false);
    }
  }

  async function onForgotPassword(values: { email: string }) {
    setError(null);
    setLoading(true);
    try {
      await forgotPassword(values.email.trim());
      setRecoveryEmail(values.email.trim());
      setRecoveryMode('confirm');
    } catch (e: unknown) {
      const raw = e instanceof Error ? e.message : 'No se pudo enviar el email';
      setError(traducirErrorCognito(raw));
    } finally {
      setLoading(false);
    }
  }

  async function onConfirmForgot(values: { code: string; newPassword: string; confirm: string }) {
    setError(null);
    if (values.newPassword !== values.confirm) {
      setError('Las Contraseñas No Coinciden');
      return;
    }
    setLoading(true);
    try {
      await confirmForgotPassword(recoveryEmail, values.code, values.newPassword);
      setRecoveryMode('success');
    } catch (e: unknown) {
      const raw = e instanceof Error ? e.message : 'Código inválido o contraseña débil';
      setError(traducirErrorCognito(raw));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'grid',
      placeItems: 'center',
      background: 'linear-gradient(135deg, #1a3a5c 0%, #0d6efd 100%)',
    }}>
      <Card style={{ width: 380, boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}>
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <img
            src="/logo-asicontadores-color.png"
            alt="ASI Contadores"
            style={{ height: 72, width: 'auto' }}
          />
          <Text type="secondary" style={{ display: 'block', marginTop: 6 }}>
            Conciliación Fiscal
          </Text>
        </div>

        {error && (
          <Alert type="error" message={error} showIcon style={{ marginTop: 16, marginBottom: 4 }} />
        )}

        {pendingChallenge ? (
          <Form<ChallengeValues> layout="vertical" onFinish={onChallenge} style={{ marginTop: 24 }}>
            <Alert
              type="info"
              showIcon
              message="Necesitas Crear una Contraseña Nueva en tu Primer Acceso."
              style={{ marginBottom: 16 }}
            />
            <Form.Item name="newPassword" label="Nueva Contraseña" rules={[{ required: true, min: 8 }]}>
              <Input.Password size="large" />
            </Form.Item>
            <Form.Item name="confirm" label="Confirmar Contraseña" rules={[{ required: true }]}>
              <Input.Password size="large" />
            </Form.Item>
            <Button type="primary" htmlType="submit" block size="large" loading={loading}>
              Establecer Contraseña
            </Button>
          </Form>
        ) : recoveryMode === 'forgot' ? (
          <Form layout="vertical" onFinish={onForgotPassword} style={{ marginTop: 24 }}>
            <Alert
              type="info"
              showIcon
              message="Recuperar Contraseña"
              description="Ingresa tu email y te enviaremos un código de verificación."
              style={{ marginBottom: 16 }}
            />
            <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
              <Input size="large" prefix={<MailOutlined />} placeholder="usuario@empresa.com" />
            </Form.Item>
            <Button type="primary" htmlType="submit" block size="large" loading={loading}>
              Enviar Código
            </Button>
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Text type="secondary">
                <Button type="link" onClick={() => setRecoveryMode('login')}>
                  Volver al Login
                </Button>
              </Text>
            </div>
          </Form>
        ) : recoveryMode === 'confirm' ? (
          <Form layout="vertical" onFinish={onConfirmForgot} style={{ marginTop: 24 }}>
            <Alert
              type="info"
              showIcon
              message={`Código enviado a ${recoveryEmail}`}
              description="Ingresa el código que recibiste y tu nueva contraseña."
              style={{ marginBottom: 16 }}
            />
            <Form.Item name="code" label="Código de Verificación" rules={[{ required: true }]}>
              <Input size="large" prefix={<SafetyOutlined />} placeholder="123456" />
            </Form.Item>
            <Form.Item name="newPassword" label="Nueva Contraseña" rules={[{ required: true, min: 8 }]}>
              <Input.Password size="large" />
            </Form.Item>
            <Form.Item name="confirm" label="Confirmar Contraseña" rules={[{ required: true }]}>
              <Input.Password size="large" />
            </Form.Item>
            <Button type="primary" htmlType="submit" block size="large" loading={loading}>
              Restablecer Contraseña
            </Button>
          </Form>
        ) : recoveryMode === 'success' ? (
          <Result
            status="success"
            title="Contraseña Actualizada"
            subTitle="Tu contraseña se ha restablecido correctamente."
            extra={[
              <Button type="primary" key="login" onClick={() => setRecoveryMode('login')}>
                Iniciar Sesión
              </Button>,
            ]}
          />
        ) : (
          <Form<LoginValues> layout="vertical" onFinish={onLogin} style={{ marginTop: 24 }}>
            <Form.Item
              name="username"
              label="Usuario"
              rules={[{ required: true, message: 'Ingresa tu Usuario' }]}
            >
              <Input
                size="large"
                prefix={<UserOutlined />}
                autoComplete="username"
                placeholder="usuario@empresa.com"
              />
            </Form.Item>
            <Form.Item name="password" label="Contraseña" rules={[{ required: true }]}>
              <Input.Password size="large" prefix={<LockOutlined />}                 autoComplete="current-password" />
            </Form.Item>
            <Button type="primary" htmlType="submit" block size="large" loading={loading}>
              Iniciar Sesión
            </Button>
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Text type="secondary">
                <Button type="link" onClick={() => setRecoveryMode('forgot')} style={{ padding: 0 }}>
                  ¿Olvidaste tu contraseña?
                </Button>
              </Text>
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">
                  ¿No tienes cuenta?{' '}
                  <Link to="/registro" style={{ color: '#0d6efd' }}>
                    Registrarse
                  </Link>
                </Text>
              </div>
            </div>
          </Form>
        )}
      </Card>
    </div>
  );
}
